/**
 * Contrato da caixa de notificações e das preferências de notificação
 * (`bff/v1/notificacoes`, `apps/api/src/modulos/notificacoes/README.md`).
 *
 * A caixa é de QUEM ESTÁ LOGADO, em qualquer área: a central de cada área, o
 * sino do cabeçalho e a tela de preferências leem daqui.
 */

export type CategoriaDeNotificacao = 'operacional' | 'comunicacional';

export type PrioridadeDeNotificacao = 'alta' | 'media' | 'baixa';

export interface Notificacao {
  id: string;
  /** Código do banco (`COBRANCA_GERADA`, `PARAMETRO_VIGENCIA`...). Rótulo em `rotuloDoTipo`. */
  tipo: string;
  categoria: CategoriaDeNotificacao;
  /** Nula quando o aviso foi gravado sem prioridade reconhecível. */
  prioridade: PrioridadeDeNotificacao | null;
  titulo: string;
  mensagem: string;
  lida: boolean;
  lidaEm: string | null;
  criadaEm: string;
}

export interface CaixaDeNotificacoes {
  itens: Notificacao[];
  /** Quantos avisos atendem ao filtro. */
  total: number;
  pagina: number;
  tamanho: number;
  /** Não lidas da caixa inteira, sem filtro — o número do sino. */
  naoLidas: number;
  /** Tipos presentes na caixa, para o filtro de tipo. */
  tipos: string[];
}

export interface FiltroDaCaixa {
  pagina: number;
  tamanho: number;
  categoria?: CategoriaDeNotificacao;
  tipo?: string;
  prioridade?: PrioridadeDeNotificacao;
  somenteNaoLidas?: boolean;
  busca?: string;
  /** Dia civil, `aaaa-mm-dd`. */
  criadaDe?: string;
  /** Dia civil, `aaaa-mm-dd`. */
  criadaAte?: string;
}

/**
 * Preferências que têm efeito hoje. Só o e-mail das comunicações opcionais: é
 * o único canal que alguma regra lê (comunicação de impacto da J-11). Ver o
 * README do módulo da API.
 */
export interface PreferenciasDeNotificacao {
  email: boolean;
  atualizadaEm: string | null;
}

/**
 * Rótulo dos tipos que o domínio grava hoje (gatilhos da 0002 e J-11). Tipo
 * novo, sem rótulo aqui, aparece pelo próprio código legível, nunca vazio.
 */
const ROTULOS_DOS_TIPOS: Readonly<Record<string, string>> = {
  APONTAMENTO_APROVADO_SME: 'Apontamento aprovado pela SME',
  APONTAMENTO_ENVIADO_SME: 'Apontamento enviado à SME',
  APONTAMENTO_PENDENTE: 'Apontamento pendente',
  APONTAMENTOS_APROVADOS_SME: 'Apontamentos aprovados pela SME',
  APONTAMENTOS_DEVOLVIDOS: 'Apontamentos devolvidos',
  APONTAMENTOS_ENVIADOS: 'Apontamentos enviados',
  COBRANCA_GERADA: 'Cobrança gerada',
  COMPETENCIA_DEFASADA: 'Competência defasada',
  COOPERATIVA_INDICACAO_RECEBIDA: 'Indicação de cooperativa recebida',
  COOPERATIVA_VINCULO: 'Vínculo com cooperativa',
  CREDENCIAMENTO_STATUS: 'Situação do credenciamento',
  DOCUMENTACAO_PENDENTE: 'Documentação pendente',
  DOCUMENTO_ENVIADO: 'Documento enviado',
  MEDICAO_APROVADA: 'Medição aprovada',
  MEDICAO_ENVIADA: 'Medição enviada',
  MEDICAO_RECEBIDA_DTP: 'Medição recebida pelo DTP',
  MENSAGEM_MEDICAO: 'Mensagem sobre medição',
  MUDANCA_STATUS: 'Mudança de situação',
  MULTA_APLICADA: 'Multa aplicada',
  NOVA_INSCRICAO_CREDENCIAMENTO: 'Nova inscrição no credenciamento',
  NOVA_OS: 'Nova ordem de serviço',
  NOVA_SOLICITACAO: 'Nova solicitação',
  PARAMETRO_VIGENCIA: 'Mudança de regra aprovada',
  PENDENCIA_DOCUMENTO: 'Pendência de documento',
  REVISAO_MEDICAO_ENCAMINHADA: 'Revisão de medição encaminhada',
  REVISAO_MEDICAO_RESPONDIDA: 'Revisão de medição respondida',
  REVISAO_MEDICAO_RESPOSTA: 'Resposta à revisão de medição',
  REVISAO_MEDICAO_SOLICITADA: 'Revisão de medição solicitada',
  SLA_APONTAMENTO_VENCIDO: 'Prazo de apontamento vencido',
  SME_DEMANDA_DTP: 'Demanda da SME ao DTP',
  SME_DEMANDA_RESPONDIDA: 'Demanda da SME respondida',
  SOLICITACAO_EM_ANALISE: 'Solicitação em análise',
  SUBSTITUICAO_RECURSO: 'Substituição de recurso',
};

export function rotuloDoTipo(tipo: string): string {
  const conhecido = ROTULOS_DOS_TIPOS[tipo];
  if (conhecido) return conhecido;
  if (!tipo) return 'Aviso';
  const legivel = tipo.replace(/_/g, ' ').toLowerCase();
  return legivel.charAt(0).toUpperCase() + legivel.slice(1);
}
